import { createSlModal, getSlModal, destroySlModal } from '../../sl_modal';
import {
  getProductById,
  checkProductSetStock,
} from '../../../service/productService';
import ProductSetModel from '../models/product_set';
import ProductSetItemModel from '../models/product_set_item';
import '../product_set_picker_column_layout';
import '../product_set_picker_payment';
import '../product_set_item_picker';
import '../product_set_picker_footer';
import '../product_set_picker_selected';
import '../../quantity_select';
import { checkCheckoutReady } from '../../../utils/merchantData';
import { getCartInitData } from '../../../utils/cart';
import './index.scss';

const ONE_PAGE_STORE_ID = getCartInitData()?.extraMainConfig?.onePageStoreId;

const SELECTED_INFO_MODAL_ID = 'product-set-picker-selected-modal';
const checkFeatureKey = (featureKey) =>
  window.mainConfig.merchantData.rollout_keys.includes(featureKey);

export const ERROR_TYPES = {
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  PRODUCT_NOT_FOUND: 'PRODUCT_NOT_FOUND',
  OUT_OF_MAX_ORDER_QUANTITY: 'OUT_OF_MAX_ORDER_QUANTITY',
};

export class ProductSetPicker extends HTMLElement {
  constructor() {
    super();
    this.isInitialized = false;
    this.product = window.QUICK_CART_MODAL_DATA.product;
    this.miniCart = document.querySelector('mini-cart');
    this.useOnePageStoreCheckout = checkFeatureKey('one_page_store_checkout');
    this.cartDrawer = document.querySelector('cart-drawer');
    // Only one is allowed to exist, remove similar picker
    this.removeOtherPickers();
  }
  connectedCallback() {
    this.model = new ProductSetModel(window.QUICK_CART_MODAL_DATA.product);
    window.requestAnimationFrame(() => {
      this.render();
      this.initSendTracker();
    });
  }
  initSendTracker() {
    const { trackerService } = this.getNgServices();
    const { product } = this;
    const { price, price_sale } = product;
    const dollars = price_sale?.dollars || price?.dollars || 0;
    trackerService.gaSendDetails(product, null, product.child_products);
    trackerService.fbSendViewContent(this.product, dollars);
    trackerService.track({
      type: trackerService.generalEventType.VIEW_CONTENT,
      data: {
        product: this.product,
        value: dollars,
      },
    });
  }

  render() {
    this.renderInitialized();
    this.renderRequiredQuantity();
    this.renderItemPickers();
    this.renderSelected();
    this.renderPayment();
    this.renderFooter();
  }
  renderInitialized() {
    if (this.isInitialized) {
      return;
    }
    this.isInitialized = true;
    this.renderSelectedModal();
  }
  removeOtherPickers() {
    const $pickers = document.querySelectorAll('product-set-picker');
    $pickers.forEach(($picker) => {
      if ($picker?.product?._id !== this.product._id) {
        $picker.onDestroy();
      }
    });
  }
  renderSelectedModal() {
    const { hasVariationsChildProduct, allowAddToCart } = this.model;
    if (!hasVariationsChildProduct || !allowAddToCart) {
      document.querySelector('product-set-picker-selected').remove();
      return;
    }
    const isMobile = document.body.clientWidth <= 768;
    if (isMobile) {
      destroySlModal(SELECTED_INFO_MODAL_ID);
      const $modal = createSlModal(SELECTED_INFO_MODAL_ID, {
        containerClass: 'product-set-picker-selected__modal-container',
      });
      document.querySelector('product-set-picker-selected').remove();
      $modal.getContainer().innerHTML =
        '<product-set-picker-selected></product-set-picker-selected>';
    }
  }
  renderPayment() {
    const $payments = document.querySelectorAll('product-set-picker-payment');
    $payments.forEach(($payment) => $payment.updateModel(this.model));
  }
  renderRequiredQuantity() {
    const { mustSelectNum } = this.model;
    const $requiredQuantity = this.querySelector('.js-required-quantity');
    $requiredQuantity.innerHTML = window.shopline.i18next.t(
      'Please select count of items',
      {
        n: mustSelectNum,
        ns: 'translation',
      },
    );
  }
  renderItemPickers() {
    const $itemPickers = this.querySelectorAll('product-set-item-picker');

    $itemPickers.forEach(($itemPicker) => {
      const childProductId = $itemPicker.getAttribute('data-product-id');
      const { childProductsMap, selected } = this.model;
      $itemPicker.updateModel({
        product: childProductsMap[childProductId],
        selected: selected[childProductId],
        disabled: !checkCheckoutReady() || !this.model.allowAddToCart,
      });
    });
  }
  renderSelected() {
    const { hasVariationsChildProduct, allowAddToCart } = this.model;
    if (!hasVariationsChildProduct || !allowAddToCart) {
      return;
    }
    const $selected = document.querySelector('product-set-picker-selected');
    $selected.updateModel(this.model);
  }
  renderFooter() {
    document
      .querySelectorAll('product-set-picker-footer')
      .forEach(($footer) => $footer.updateModel(this.model));
  }
  renderChildProductError(childProductId, error) {
    this.querySelector(
      `product-set-item-picker[data-product-id="${childProductId}"]`,
    )?.setError?.(error);
  }
  onSelectedChange(childProductId, variantKey, num) {
    this.model.onSelectedChange(childProductId, variantKey, num);
    this.render();

    if (this.model.computedFinishSelectForChildProduct(childProductId)) {
      const nextMustSelectChildProduct =
        this.model.computedNextMustSelectChildProduct(childProductId);

      if (nextMustSelectChildProduct) {
        window.setTimeout(() => {
          const $productPicker = document.querySelector(
            `product-set-item-picker[data-product-id="${nextMustSelectChildProduct._id}"]`,
          );

          // scroll to the next child-product 300 milliseconds after selected a child-product
          this.closest('product-set-picker-column-layout')?.scrollToElement?.(
            $productPicker,
          );
        }, 300);
      }
    }
  }
  onShowSelectedInfo() {
    const $modal = getSlModal(SELECTED_INFO_MODAL_ID);
    if ($modal) {
      $modal.onShow();
    } else {
      this.closest('product-set-picker-column-layout')?.scrollToElement?.(
        this.querySelector('product-set-picker-selected'),
      );
    }
  }
  onAddToCart() {
    const { selected } = this.model;
    this.model.loading = true;
    this.render();
    let lastestProduct = null;
    getProductById(this.model.product._id)
      .then((productResponse) => {
        lastestProduct = productResponse?.data?.data;
        if (!lastestProduct) {
          return Promise.reject(new Error(ERROR_TYPES.PRODUCT_NOT_FOUND));
        }
        return this.onCheckChildProductsStock(lastestProduct);
      })
      .then(() =>
        checkProductSetStock(
          this.model.product._id,
          selected,
          ONE_PAGE_STORE_ID,
        ).then((response) => response.data),
      )
      .then((stockResponse) => this.onCheckStock(stockResponse))
      .then(() => this.onUpdateCart(lastestProduct))
      .then(() => {
        this.onClose();
        // NOTE: fixed the problem of cart-panel opening mask position error in some iOS Safari versions, see https://shopline.atlassian.net/browse/SL-45729
        window.setTimeout(() => this.openMiniCart(), 200);
      })
      .catch((errorResponse) => {
        const error = errorResponse?.error;
        const errorMessage = error?.message;
        const errorData = errorResponse?.data;
        if (errorMessage === ERROR_TYPES.OUT_OF_STOCK) {
          this.model.error = window.shopline.i18next.t('Not enough stock', {
            ns: 'translation',
          });
          return;
        }
        if (errorMessage === ERROR_TYPES.OUT_OF_MAX_ORDER_QUANTITY) {
          this.model.error = window.shopline.i18next.t(
            'Up to n set(s) per order',
            {
              ns: 'translation',
              n: errorData.max_order_quantity,
            },
          );
          return;
        }
        console.error('onAddtoCart waring:', errorResponse);
      })
      .finally(() => {
        this.model.loading = false;
        this.render();
      });
  }
  onCheckStock(stockResponse) {
    const {
      unlimited_quantity,
      left_items_quantity,
      max_order_quantity,
      total_variations_cart_qty,
    } = stockResponse;

    if (
      max_order_quantity > 0 &&
      total_variations_cart_qty >= max_order_quantity
    ) {
      return Promise.reject({
        error: new Error(ERROR_TYPES.OUT_OF_MAX_ORDER_QUANTITY),
        data: {
          max_order_quantity,
        },
      });
    }

    if (unlimited_quantity) {
      return true;
    }

    if (!left_items_quantity) {
      return Promise.reject({
        error: new Error(ERROR_TYPES.OUT_OF_STOCK),
      });
    }

    return true;
  }
  onCheckChildProductsStock(lastestProduct) {
    const { child_products } = lastestProduct;
    let verifyProductStockSuccess = true;
    const { selected } = this.model;
    const childProductModelMap = child_products.reduce(
      (current, childProduct) => {
        current[childProduct._id] = new ProductSetItemModel(childProduct);
        return current;
      },
      {},
    );
    Object.entries(selected).forEach((data) => {
      const [productId, value] = data;
      let verifySuccess = true;
      const { selectedNum, selectedVariationsNum } = value;
      const vantionKeys = Object.keys(selectedVariationsNum || {});
      if (vantionKeys.length > 0) {
        verifySuccess = vantionKeys.every((vantionKey) =>
          childProductModelMap[productId].verifySelectedNum(
            selectedVariationsNum[vantionKey],
            vantionKey,
          ),
        );
      } else {
        verifySuccess =
          childProductModelMap[productId].verifySelectedNum(selectedNum);
      }
      if (!verifySuccess) {
        verifyProductStockSuccess = false;
        this.renderChildProductError(
          productId,
          window.shopline.i18next.t('Not enough stock', {
            ns: 'translation',
          }),
        );
      }
    });
    if (verifyProductStockSuccess) {
      return Promise.resolve(true);
    }
    return Promise.reject({
      error: new Error(ERROR_TYPES.OUT_OF_STOCK),
    });
  }
  getNgServices() {
    const injector = angular.element('body').injector();
    return {
      productService: injector.get('productService'),
      productPreorderService: injector.get('productPreorderService'),
      cartService: injector.get('cartService'),
      trackerService: injector.get('trackerService'),
      slFeatureService: injector.get('slFeatureService'),
      $rootScope: injector.get('$rootScope'),
      $cookies: injector.get('$cookies'),
    };
  }
  updateChildProductData(childProductId, childVariationKey, data) {
    this.model.updateChildProductData(childProductId, childVariationKey, data);
    this.render();
  }
  onUpdateCart(lastestProduct) {
    const { cartService } = this.getNgServices();
    let promise = null;
    const { selected, product } = this.model;
    const selectedChildProducts = [];
    Object.values(selected).forEach((childSelected) => {
      const { selectedNum, selectedVariationsNum } = childSelected;
      if (!selectedNum) {
        return;
      }
      const vantionsSelectedList = Object.entries(selectedVariationsNum || {});
      if (vantionsSelectedList.length > 0) {
        vantionsSelectedList.forEach(([vantionKey, vantionQuantity]) => {
          selectedChildProducts.push({
            child_product_id: childSelected.id,
            child_variation_id: vantionKey,
            quantity: vantionQuantity,
          });
        });
      } else {
        selectedChildProducts.push({
          child_product_id: childSelected.id,
          quantity: selectedNum,
        });
      }
    });

    if (this.useOnePageStoreCheckout) {
      const params = {
        item: {
          product_id: this.model.product._id,
          type: 'product_set',
          quantity: 1,
          item_data: {
            selected_child_products: selectedChildProducts,
          },
        },
      };

      promise = this.miniCart.add(params).then((response) => ({
        product: { ...lastestProduct },
        fbPixelEventId: response.headers.get('fb-pixel-event-id'),
      }));
    } else {
      const {
        price_sale,
        price,
        blacklisted_delivery_option_ids,
        blacklisted_payment_option_ids,
      } = product;
      const cartItemData = {
        customCartOptions: undefined,
        blacklisted_delivery_option_ids,
        blacklisted_payment_option_ids,
        productSetData: selectedChildProducts,
        quantity: 1,
        type: 'product_set',
        value: price_sale.dollars || price.dollars,
        variation: null,
      };
      const cartEventOptions = {
        button_type: 'add_to_cart',
      };
      promise = cartService
        .addItem(product._id, cartItemData, cartEventOptions, {
          skip_calculate_order: true,
        })
        .then((data) => ({ data, product }));
    }

    return promise.then(({ product, data, fbPixelEventId }) => {
      this.updateMiniCart().then(() => {
        if (this.useOnePageStoreCheckout) {
          const cartData = this.miniCart.store.data;

          window.shopline.onePageStore?.onTrackEvent('AddItem', {
            itemInfo: {
              product_id: product._id,
              variation_id: product.variant_id,
              type: product.type,
              quantity: 1,
            },
            cartData: cartData,
            fbPixelEventId: fbPixelEventId,
          });
          return Promise.resolve(true);
        }

        const { $rootScope, trackerService } = this.getNgServices();
        if (checkFeatureKey('data_layer_info')) {
          $rootScope.$emit('add.item.to.cart', {
            ...cartService.getGaItemData({
              product: product,
              variant: null,
            }),
            value: this.model.checkoutPriceMoney?.dollars,
          });
        }
        if (data?.event_id) {
          trackerService.track({
            type: trackerService.generalEventType.ADD_TO_CART,
            data: {
              items: [
                {
                  product,
                  value: this.model.checkoutPriceMoney?.dollars,
                  quantity: 1,
                  variationSelected: null,
                },
              ],
              eventId: data?.event_id,
            },
          });
        }
      });
      return Promise.resolve(true);
    });
  }
  updateMiniCart() {
    if (this.useOnePageStoreCheckout) {
      window.shopline.onePageStore?.onCartUpdate();
      return this.miniCart.update();
    } else {
      return Promise.resolve(true);
    }
  }
  openMiniCart() {
    if (this.useOnePageStoreCheckout) {
      this.cartDrawer.showWithAutoHide();
    } else {
      jQuery.sidr('open', 'cart-panel');
    }
  }
  onClose() {
    getSlModal(this.model.product._id).onHide();
    getSlModal(SELECTED_INFO_MODAL_ID)?.onHide?.();
  }
  onDestroy() {
    destroySlModal(SELECTED_INFO_MODAL_ID);
    if (getSlModal(this.product._id)) {
      destroySlModal(this.product._id);
    } else {
      this.remove();
    }
  }
}

if (!customElements.get('product-set-picker')) {
  customElements.define('product-set-picker', ProductSetPicker);
}
